<template>
    <div class="flex items-center gap-1 h-full" :class="textColor">
        <UTooltip
            v-if="activeStatuses.length > 1"
            :text="tooltipText"
            :delay-duration="0"
            :ui="{ content: 'w-72 h-fit', text: 'whitespace-pre-line p-1' }"
        >
            <div class="flex items-center gap-1 cursor-pointer">
                <i v-for="status in activeStatuses" :key="status.label" :class="[status.icon, 'text-xl']" />
            </div>
        </UTooltip>

        <div v-else-if="activeStatuses.length === 1" class="flex items-center gap-1">
            <i :class="[activeStatuses[0].icon, 'text-xl']" />
            <span>{{ activeStatuses[0].label }}</span>
        </div>

        <UButton
            icon="i-ph-dots-three-vertical"
            color="white"
            class="text-base-content ml-auto"
            variant="subtle"
            @click="showContextMenu"
        />
    </div>
</template>

<script setup lang="ts">

import {computed} from 'vue'
import {GridApi, RowNode} from 'ag-grid-community'
import {CsvDataRow} from '@/incoming-balance/types/CsvDataRow.type'
import useTrialBalance from '@/incoming-balance/tribal.composable'

const { isAccountMissing, isAccountMisnamed, accountNeedsActivation, isVatMismatch } = useTrialBalance()

const props = defineProps({
    params: {
        type: Object as () => {
            data: CsvDataRow,
            api: GridApi,
            node: RowNode,
        },
        required: true
    }
})

const csvRow = computed<CsvDataRow>(() => props.params.data)

const allStatuses = computed(() => [
    {
        active: isAccountMissing(csvRow.value),
        label: 'Konto mangler',
        icon: 'i-ph-warning-circle',
        isError: true
    },
    {
        active: isVatMismatch(csvRow.value),
        label: 'Avvikende MVA-kode',
        icon: 'i-ph-warning text-warning',
        isError: false
    },
    {
        active: isAccountMisnamed(csvRow.value),
        label: 'Avvikende navn',
        icon: 'i-ph-warning text-warning',
        isError: false
    },
    {
        active: accountNeedsActivation(csvRow.value),
        label: 'Konto er inaktiv',
        icon: 'i-ph-warning text-warning',
        isError: false
    }
])

const activeStatuses = computed(() => allStatuses.value.filter(status => status.active))

const hasError = computed(() => activeStatuses.value.some(status => status.isError))
const hasWarning = computed(() => activeStatuses.value.some(status => !status.isError))

const tooltipText = computed(() => activeStatuses.value.map(status => status.label).join('\n'))

const textColor = computed(() => {
    if (hasError.value) return 'text-error'
    if (hasWarning.value) return 'text-warning'
    return ''
})

const showContextMenu = () => {
    props.params?.api.showContextMenu({
        source: 'ui',
        rowNode: props.params?.node,
        column: props.params?.api.getColumn('error'),
        value: null
    })
}

</script>

<style scoped>

</style>